import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createHousingExpense,
  updateHousingExpense,
} from "../../lib/api-modules/housing-expenses.js";
import {
  HELOC_USE_PURPOSE_LABELS,
  HOUSING_EXPENSE_TYPE_LABELS,
  type CreateHousingExpenseInput,
  type HelocUsePurpose,
  type HousingExpense,
  type HousingExpenseType,
  type UpdateHousingExpenseInput,
} from "../../lib/api-modules/types.js";
import { ErrorMessage } from "../auth/error-message.js";

const TYPE_OPTIONS: ReadonlyArray<{ value: HousingExpenseType; label: string }> = (
  Object.keys(HOUSING_EXPENSE_TYPE_LABELS) as HousingExpenseType[]
).map((v) => ({ value: v, label: HOUSING_EXPENSE_TYPE_LABELS[v] }));

const HELOC_OPTIONS: ReadonlyArray<{ value: HelocUsePurpose; label: string }> = (
  Object.keys(HELOC_USE_PURPOSE_LABELS) as HelocUsePurpose[]
).map((v) => ({ value: v, label: HELOC_USE_PURPOSE_LABELS[v] }));

interface HousingExpenseFormProps {
  readonly mode: "create" | "edit";
  readonly unitId: string;
  readonly expense?: HousingExpense;
  readonly onClose: () => void;
}

export function HousingExpenseForm({
  mode,
  unitId,
  expense,
  onClose,
}: HousingExpenseFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";

  const [expenseType, setExpenseType] = useState<HousingExpenseType>(
    expense?.expense_type ?? "maintenance",
  );
  const [amountDollars, setAmountDollars] = useState<string>(
    expense ? (expense.amount_cents / 100).toString() : "",
  );
  const [paymentDate, setPaymentDate] = useState<string>(
    expense?.payment_date ?? new Date().toISOString().slice(0, 10),
  );
  const [periodCovered, setPeriodCovered] = useState<string>(
    expense?.period_covered ?? "",
  );
  const [vendorOrPayee, setVendorOrPayee] = useState<string>(
    expense?.vendor_or_payee ?? "",
  );
  const [mortgageInterestDollars, setMortgageInterestDollars] = useState<string>(
    expense?.deductible_mortgage_interest_cents !== undefined
      ? (expense.deductible_mortgage_interest_cents / 100).toString()
      : "",
  );
  const [propertyTaxDollars, setPropertyTaxDollars] = useState<string>(
    expense?.deductible_property_tax_cents !== undefined
      ? (expense.deductible_property_tax_cents / 100).toString()
      : "",
  );
  const [nonDeductibleDollars, setNonDeductibleDollars] = useState<string>(
    expense?.non_deductible_portion_cents !== undefined
      ? (expense.non_deductible_portion_cents / 100).toString()
      : "",
  );
  const [helocPurpose, setHelocPurpose] = useState<HelocUsePurpose | "">(
    expense?.heloc_use_purpose ?? "",
  );
  const [notes, setNotes] = useState<string>(expense?.notes ?? "");
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Deductibility breakdown surfaces only on maintenance + hoa_fee +
  // property_tax + mortgage_payment rows. The board's Form 1098-equivalent
  // letter typically gives all three numbers.
  const showDeductibilityBreakdown =
    expenseType === "maintenance" ||
    expenseType === "hoa_fee" ||
    expenseType === "property_tax" ||
    expenseType === "mortgage_payment";

  const showHelocPurpose = expenseType === "heloc_payment";

  const mutation = useMutation({
    mutationFn: async () => {
      const amount = parseDollars(amountDollars);
      if (amount === undefined)
        throw new Error("Amount is required (in dollars)");
      if (!paymentDate) throw new Error("Payment date is required");

      const baseInput: CreateHousingExpenseInput = {
        expense_type: expenseType,
        amount_cents: amount,
        payment_date: paymentDate,
      };
      if (periodCovered.trim()) baseInput.period_covered = periodCovered.trim();
      if (vendorOrPayee.trim()) baseInput.vendor_or_payee = vendorOrPayee.trim();
      const mi = parseDollars(mortgageInterestDollars);
      if (mi !== undefined) baseInput.deductible_mortgage_interest_cents = mi;
      const pt = parseDollars(propertyTaxDollars);
      if (pt !== undefined) baseInput.deductible_property_tax_cents = pt;
      const nd = parseDollars(nonDeductibleDollars);
      if (nd !== undefined) baseInput.non_deductible_portion_cents = nd;
      if (helocPurpose) baseInput.heloc_use_purpose = helocPurpose;
      if (notes.trim()) baseInput.notes = notes.trim();

      if (isEdit && expense) {
        // PATCH cannot change payment_date (SK rewrite); the form
        // doesn't expose payment_date for edits.
        const patch: UpdateHousingExpenseInput = {};
        patch.expense_type = baseInput.expense_type;
        patch.amount_cents = baseInput.amount_cents;
        if (baseInput.period_covered !== undefined)
          patch.period_covered = baseInput.period_covered;
        if (baseInput.vendor_or_payee !== undefined)
          patch.vendor_or_payee = baseInput.vendor_or_payee;
        if (baseInput.deductible_mortgage_interest_cents !== undefined)
          patch.deductible_mortgage_interest_cents =
            baseInput.deductible_mortgage_interest_cents;
        if (baseInput.deductible_property_tax_cents !== undefined)
          patch.deductible_property_tax_cents =
            baseInput.deductible_property_tax_cents;
        if (baseInput.non_deductible_portion_cents !== undefined)
          patch.non_deductible_portion_cents =
            baseInput.non_deductible_portion_cents;
        if (baseInput.heloc_use_purpose !== undefined)
          patch.heloc_use_purpose = baseInput.heloc_use_purpose;
        if (baseInput.notes !== undefined) patch.notes = baseInput.notes;
        return updateHousingExpense(
          unitId,
          expense.payment_date,
          expense.expense_id,
          patch,
        );
      }
      return createHousingExpense(unitId, baseInput);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["housing-expenses", unitId],
      });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't save expense.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit housing expense" : "Add housing expense"}
      className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {isEdit ? "Edit expense" : "Add expense"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-medium">Type</label>
          <select
            className={inputClass}
            value={expenseType}
            onChange={(e) => setExpenseType(e.target.value as HousingExpenseType)}
            disabled={mutation.isPending}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Amount ($)</label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Payment date</label>
          <input
            type="date"
            className={inputClass}
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            disabled={mutation.isPending || isEdit}
          />
          {isEdit && (
            <p className="mt-1 text-xs text-zinc-500">
              Date is the SK; delete + re-create to change.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">
            Period covered{" "}
            <span className="text-zinc-500">(e.g. "Jan 2026" or "2025")</span>
          </label>
          <input
            className={inputClass}
            value={periodCovered}
            onChange={(e) => setPeriodCovered(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Vendor / payee <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            className={inputClass}
            value={vendorOrPayee}
            onChange={(e) => setVendorOrPayee(e.target.value)}
            placeholder="Building management LLC"
            disabled={mutation.isPending}
          />
        </div>
      </div>

      {showDeductibilityBreakdown && (
        <section className="grid grid-cols-1 gap-3 rounded-md border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-900 dark:bg-amber-950/40 sm:grid-cols-3">
          <header className="text-xs font-medium sm:col-span-3">
            Deductibility breakdown{" "}
            <span className="text-zinc-500">
              (from your board's annual letter — Schedule A inputs)
            </span>
          </header>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Mortgage interest portion ($)
            </label>
            <input
              type="number"
              step="0.01"
              className={inputClass}
              value={mortgageInterestDollars}
              onChange={(e) => setMortgageInterestDollars(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Property tax portion ($)
            </label>
            <input
              type="number"
              step="0.01"
              className={inputClass}
              value={propertyTaxDollars}
              onChange={(e) => setPropertyTaxDollars(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Non-deductible portion ($)
            </label>
            <input
              type="number"
              step="0.01"
              className={inputClass}
              value={nonDeductibleDollars}
              onChange={(e) => setNonDeductibleDollars(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
        </section>
      )}

      {showHelocPurpose && (
        <section className="rounded-md border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-900 dark:bg-amber-950/40">
          <label className="mb-1 block text-xs font-medium">
            HELOC use purpose
          </label>
          <select
            className={inputClass}
            value={helocPurpose}
            onChange={(e) =>
              setHelocPurpose(e.target.value as HelocUsePurpose | "")
            }
            disabled={mutation.isPending}
          >
            <option value="">— select to record TCJA tracing-rule purpose —</option>
            {HELOC_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-zinc-500">
            Phase 7 (Tax) uses this for the post-TCJA Schedule A
            deductibility check. Only "home improvement" qualifies.
          </p>
        </section>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium">
          Notes <span className="text-zinc-500">(optional)</span>
        </label>
        <textarea
          className={inputClass}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={mutation.isPending}
          maxLength={2000}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : isEdit ? "Save expense" : "Add expense"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";

function parseDollars(s: string): number | undefined {
  const trimmed = s.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}
